import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type {
  ComfortInput,
  ConstructieTabelModus,
  IsolatieProfiel,
  Luchtdichtheid,
  TypeGlas,
  TypeWoning,
  UserSegment,
} from '@comforttool/comfortcore';
import {
  ArrowRight,
  Building2,
  Check,
  CloudSun,
  Cpu,
  Gauge,
  Layers,
  MapPin,
  Sparkles,
  Table2,
  Thermometer,
  Zap,
} from 'lucide-react';
import { Seo } from '../components/Seo';
import { Breadcrumbs } from '../components/Breadcrumbs';
import {
  applyClimateYearlyToResult,
  computeComfortEngine,
  ENGINE_TRANSPARENCY_BULLETS,
  normalizeComfortInput,
  validateComfortInput,
  TYPE_WONING_LABELS,
} from '../lib/comfortCalc';
import { fetchClimateForPostcode } from '../lib/api';
import type { BundleTier } from '../lib/pricing';
import {
  FLOOR_BAND_MAX_XL_M2,
  PRICING_PARTICULIER_BASIS_INCL_BTW,
  PRICING_PARTICULIER_INCL_BTW,
  PRICING_ZAKELIJK_BASIS_EX_BTW,
  PRICING_ZAKELIJK_EX_BTW,
  bandOmschrijving,
  floorPriceBand,
  formatPriceEur,
  prijsSnapshotVoorInvoer,
} from '../lib/pricing';

gsap.registerPlugin(ScrollTrigger);

const ISOLATIE_STEPS: IsolatieProfiel[] = ['Slecht', 'Matig', 'Geschat op bouwjaar', 'Goed', 'Zeer goed'];

const GLAS_OPTS: TypeGlas[] = ['Dubbel glas', 'HR++ glas', 'Triple glas'];

const WONING_OPTS: TypeWoning[] = ['Appartement', 'Tussenwoning', 'Hoekwoning', 'Vrijstaand'];

const CONSTRUCTIE_OPTS: { id: ConstructieTabelModus; label: string; hint: string }[] = [
  { id: 'standaard_bouwjaar', label: 'Standaard op bouwjaar', hint: 'Proxy zoals in de scan' },
  { id: 'issoproxy_streng', label: 'Strakker geïsoleerd', hint: 'Gunstig scenario' },
  { id: 'issoproxy_versoepeld', label: 'Meer warmteverlies', hint: 'Voorzichtig scenario' },
];

const LUCHT_OPTS: Luchtdichtheid[] = ['Loos', 'Normaal', 'Strak'];

const POSTCODE_OPTS = [
  { pc: '3511', label: 'Utrecht (binnenstad)' },
  { pc: '9721', label: 'Groningen' },
  { pc: '6211', label: 'Maastricht' },
  { pc: '1017', label: 'Amsterdam' },
] as const;

const SCAN_FEATURES = [
  {
    titel: 'Per ruimte',
    body: 'Vloer, buitenwand, ramen en wand naar onverwarmd — de engine verdeelt lasten en jaarschatting per kamer, met een “Jip en Janneke”-samenvatting.',
  },
  {
    titel: 'Klimaat op postcode',
    body: 'Vier cijfers van je postcode → Open‑Meteo-archief: graaddagen schalen de jaartotalen t.o.v. de NL-referentie (zelfde logica als na “Bereken” in de scan).',
  },
  {
    titel: 'Uurlijkse simulatie',
    body: 'Na je echte berekening: optioneel uurlijks thermisch beeld (Worker-API), met traagheid licht/normaal/zwaar — niet in deze demo om API-limieten te sparen.',
  },
  {
    titel: 'Rapport & export',
    body: 'Comfort Plus: uitgebreid leesrapport (AI-tekst op basis van je data). Gratis CSV-overzicht, print/PDF-vriendelijk scherm, checkout Stripe.',
  },
];

function buildDemoRuimtes(m2: number): ComfortInput['ruimtes'] {
  if (m2 < 100) {
    return [];
  }
  const a = Math.round(m2 * 0.48 * 100) / 100;
  const b = Math.round(m2 * 0.32 * 100) / 100;
  const c = Math.round((m2 - a - b) * 100) / 100;
  return [
    {
      id: 'demo-woonkamer',
      naam: 'Woonkamer',
      vloeroppervlakte_m2: a,
      positie: 'half_buiten',
      verdieping: 1,
      buitenwand_m2: 0,
      ramen_m2: 0,
      wand_naar_ongverwarmd_m2: 0,
    },
    {
      id: 'demo-slaap',
      naam: 'Slaapgedeelte',
      vloeroppervlakte_m2: b,
      positie: 'buiten',
      verdieping: 1,
      buitenwand_m2: 0,
      ramen_m2: 0,
      wand_naar_ongverwarmd_m2: 0,
    },
    {
      id: 'demo-overig',
      naam: 'Overig',
      vloeroppervlakte_m2: Math.max(0.01, c),
      positie: 'binnen',
      verdieping: 1,
      buitenwand_m2: 0,
      ramen_m2: 0,
      wand_naar_ongverwarmd_m2: 0,
    },
  ];
}

export function DemoPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const kwReadoutRef = useRef<HTMLParagraphElement>(null);

  const [m2, setM2] = useState(165);
  const [bouwjaar, setBouwjaar] = useState(1985);
  const [isolatieIdx, setIsolatieIdx] = useState(2);
  const [typeWoning, setTypeWoning] = useState<TypeWoning>('Tussenwoning');
  const [glasType, setGlasType] = useState<TypeGlas>('Dubbel glas');
  const [segment, setSegment] = useState<UserSegment>('particulier');
  const [tier, setTier] = useState<BundleTier>('plus');
  const [constructie, setConstructie] = useState<ConstructieTabelModus>('standaard_bouwjaar');
  const [lucht, setLucht] = useState<Luchtdichtheid>('Normaal');
  const [binnenTemp, setBinnenTemp] = useState(21);
  const [postcode, setPostcode] = useState<string>('3511');
  const [multiRoom, setMultiRoom] = useState(true);
  const [warmtebrugPct, setWarmtebrugPct] = useState(8);

  const [climateLoading, setClimateLoading] = useState(false);
  const [displayResult, setDisplayResult] = useState<ReturnType<typeof computeComfortEngine> | null>(null);

  const demoInput = useMemo(() => {
    const ruimtes = multiRoom && m2 >= 100 ? buildDemoRuimtes(m2) : undefined;
    const raamPct = typeWoning === 'Appartement' ? 0.22 : typeWoning === 'Vrijstaand' ? 0.16 : 0.19;
    const partial: Partial<ComfortInput> = {
      vloeroppervlakte: m2,
      plafondhoogte: 2.65,
      bouwlagen: m2 > 240 ? 2 : 1,
      bouwjaar,
      type_woning: typeWoning,
      type_glas: glasType,
      user_segment: segment,
      postcode,
      raamoppervlakte: Math.round(Math.max(10, m2 * raamPct) * 10) / 10,
      temperatuur: binnenTemp,
      personen: 3,
      zonwering: 'Deels',
      isolatie_profiel: ISOLATIE_STEPS[Math.min(ISOLATIE_STEPS.length - 1, Math.max(0, isolatieIdx))],
      renovatie_scenario: 'Geen',
      luchtdichtheid: lucht,
      constructie_tabel: constructie,
      warmtebrug_toeslag_frac: Math.min(0.35, Math.max(0, warmtebrugPct / 100)),
      ruimtes,
    };
    return normalizeComfortInput(partial);
  }, [
    m2,
    bouwjaar,
    isolatieIdx,
    typeWoning,
    glasType,
    segment,
    postcode,
    binnenTemp,
    lucht,
    constructie,
    warmtebrugPct,
    multiRoom,
  ]);

  const engineErrors = useMemo(() => validateComfortInput(demoInput), [demoInput]);

  const baseEngineResult = useMemo(() => {
    if (engineErrors.length) return null;
    try {
      return computeComfortEngine(demoInput);
    } catch {
      return null;
    }
  }, [demoInput, engineErrors.length]);

  useEffect(() => {
    if (!baseEngineResult) {
      setDisplayResult(null);
      return;
    }
    let cancelled = false;
    setClimateLoading(true);
    void fetchClimateForPostcode(demoInput.postcode).then((cr) => {
      if (cancelled) return;
      if (cr.ok) {
        setDisplayResult(applyClimateYearlyToResult(baseEngineResult, cr.climate, demoInput));
      } else {
        setDisplayResult(baseEngineResult);
      }
      setClimateLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [baseEngineResult, demoInput]);

  const band = useMemo(() => floorPriceBand(m2, false), [m2]);
  const bandLabel = useMemo(() => bandOmschrijving(band, false), [band]);

  const snapshot = useMemo(
    () =>
      prijsSnapshotVoorInvoer({
        user_segment: segment,
        vloeroppervlakte: m2,
        type_woning: typeWoning,
        bundleTier: tier,
      }),
    [segment, m2, typeWoning, tier]
  );

  const priceBasis =
    band !== 'QUOTE' ? (segment === 'particulier' ? PRICING_PARTICULIER_BASIS_INCL_BTW[band] : PRICING_ZAKELIJK_BASIS_EX_BTW[band]) : null;
  const pricePlus =
    band !== 'QUOTE' ? (segment === 'particulier' ? PRICING_PARTICULIER_INCL_BTW[band] : PRICING_ZAKELIJK_EX_BTW[band]) : null;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('.demo-reveal').forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
          y: 22,
          opacity: 0,
          duration: 0.55,
          ease: 'power2.out',
        });
      });
      gsap.from('.demo-hero-visual', {
        scrollTrigger: { trigger: '.demo-hero-visual', start: 'top 90%' },
        scale: 0.96,
        opacity: 0,
        duration: 0.65,
        ease: 'power2.out',
      });
    }, root);
    return () => ctx.revert();
  }, []);

  const kwDisplay = displayResult?.warmtelastKW ?? 0;
  useLayoutEffect(() => {
    const el = kwReadoutRef.current;
    if (!el) return;
    const t = { v: parseFloat(el.textContent) || 0 };
    gsap.to(t, {
      v: kwDisplay,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        el.textContent = `${t.v.toFixed(2)} kW`;
      },
    });
  }, [kwDisplay]);

  const gaugePct = Math.min(100, Math.max(6, (kwDisplay / 32) * 100));

  const ruimteRows = displayResult?.ruimteLasten?.slice(0, 6) ?? [];

  return (
    <>
      <Seo
        path="/demo"
        title="Interactieve demo"
        description="Live demo met de echte ComfortTool™-rekenkern: warmte- en koellast, jaartotalen met klimaat op postcode, per ruimte — daarna start je de volledige scan."
        keywords="ComfortTool demo, rekenkern, warmtelast, scan, klimaat postcode"
      />
      <div ref={rootRef} className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Demo' }]} />

        <header className="demo-reveal mt-8 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-ct-accent">Live demo</p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-ct-text sm:text-4xl lg:text-[2.5rem]">
              Zelfde kern als de scan — in het kort
            </h1>
            <p className="mt-4 max-w-xl text-pretty text-sm leading-relaxed text-ct-muted sm:text-base">
              Hier draait <strong className="text-ct-text">computeComfortEngine</strong> op jouw demoinvoer. Daarna halen we
              (zoals in de app) <strong className="text-ct-text">klimaat op postcode</strong> op en schalen we de jaartotalen.
              Geen verzonnen kW-curve meer: wat je ziet is wat de tool rekent — nog steeds{' '}
              <strong className="text-ct-text">indicatief</strong>, geen energielabel.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/scan"
                className="inline-flex items-center gap-2 rounded-xl bg-ct-text px-6 py-3 text-sm font-bold text-ct-bg transition hover:opacity-90 dark:bg-white dark:text-slate-900"
              >
                Volledige scan-wizard
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link
                to="/prijzen"
                className="inline-flex items-center rounded-xl border border-ct-border bg-ct-surface/90 px-6 py-3 text-sm font-semibold text-ct-text transition hover:border-ct-accent/35"
              >
                Prijzen
              </Link>
            </div>
          </div>

          <div className="demo-hero-visual relative overflow-hidden rounded-3xl border border-ct-accent/25 bg-gradient-to-br from-ct-accent/[0.12] via-ct-surface to-ct-elevated/60 p-6 shadow-lg">
            <div
              className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-ct-accent/20 blur-3xl"
              aria-hidden
            />
            <div className="relative flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ct-accent/15 text-ct-accent">
                <Cpu className="h-6 w-6" aria-hidden />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-ct-muted">Warmtelast (engine)</p>
                <p ref={kwReadoutRef} className="text-3xl font-bold tabular-nums text-ct-text">
                  {kwDisplay.toFixed(2)} kW
                </p>
              </div>
            </div>
            <div className="relative mt-5 h-3 overflow-hidden rounded-full bg-ct-elevated">
              <div
                className="h-full rounded-full bg-gradient-to-r from-ct-accent to-blue-400 transition-[width] duration-300 ease-out"
                style={{ width: `${gaugePct}%` }}
              />
            </div>
            {displayResult ? (
              <p className="mt-3 text-xs leading-relaxed text-ct-muted">
                Koel {displayResult.koellastKW.toFixed(2)} kW · Piek {displayResult.pieklastKWDisplay.toFixed(2)} kW · Jaar
                warmte ca. <strong className="text-ct-text">{Math.round(displayResult.jaarverbruikWarmteKWh)}</strong> kWh
                {climateLoading ? (
                  <span> — klimaat laden…</span>
                ) : displayResult.climateContext ? (
                  <span>
                    {' '}
                    ({displayResult.climateContext.locationLabel}, HDD {Math.round(displayResult.climateContext.hdd18)})
                  </span>
                ) : null}
              </p>
            ) : (
              <p className="mt-3 text-xs text-ct-muted">Pas de invoer aan om te rekenen.</p>
            )}
          </div>
        </header>

        {engineErrors.length > 0 ? (
          <div className="demo-reveal mt-8 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-200">
            {engineErrors[0]}
          </div>
        ) : null}

        <section className="demo-reveal mt-16 rounded-3xl border border-ct-border bg-ct-surface/90 p-6 shadow-sm sm:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <Gauge className="h-5 w-5 text-ct-accent" aria-hidden />
            <h2 className="text-xl font-bold text-ct-text">Invoer — zoals in de scan (vereenvoudigd)</h2>
          </div>
          <p className="mt-2 text-sm text-ct-muted">
            Ramen worden automatisch geschat als % van de vloer (anders per woningtype). Schakel meerdere ruimtes in voor
            een <strong className="text-ct-text">echte verdeling</strong> in de tabel hieronder.
          </p>

          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <div className="space-y-6">
              <div>
                <label htmlFor="demo-m2" className="flex justify-between text-sm font-semibold text-ct-text">
                  <span>Vloeroppervlakte</span>
                  <span className="tabular-nums text-ct-accent">{m2} m²</span>
                </label>
                <input
                  id="demo-m2"
                  type="range"
                  min={45}
                  max={FLOOR_BAND_MAX_XL_M2}
                  step={5}
                  value={m2}
                  onChange={(e) => setM2(Number(e.target.value))}
                  className="mt-3 h-2 w-full cursor-pointer accent-ct-accent"
                />
                <p className="mt-1 text-xs text-ct-muted">Prijzenband: {band === 'QUOTE' ? 'offerte' : `${band} — ${bandLabel}`}</p>
              </div>

              <div>
                <label htmlFor="demo-bj" className="flex justify-between text-sm font-semibold text-ct-text">
                  <span>Bouwjaar</span>
                  <span className="tabular-nums text-ct-accent">{bouwjaar}</span>
                </label>
                <input
                  id="demo-bj"
                  type="range"
                  min={1930}
                  max={2024}
                  step={1}
                  value={bouwjaar}
                  onChange={(e) => setBouwjaar(Number(e.target.value))}
                  className="mt-3 h-2 w-full cursor-pointer accent-ct-accent"
                />
              </div>

              <div>
                <p className="text-sm font-semibold text-ct-text">Isolatieprofiel (engine)</p>
                <input
                  type="range"
                  min={0}
                  max={ISOLATIE_STEPS.length - 1}
                  step={1}
                  value={isolatieIdx}
                  onChange={(e) => setIsolatieIdx(Number(e.target.value))}
                  className="mt-3 h-2 w-full cursor-pointer accent-ct-accent"
                />
                <p className="mt-2 text-xs font-medium text-ct-accent">{ISOLATIE_STEPS[isolatieIdx]}</p>
              </div>

              <div>
                <p className="text-sm font-semibold text-ct-text">Type woning</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {WONING_OPTS.map((w) => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setTypeWoning(w)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        typeWoning === w
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {TYPE_WONING_LABELS[w]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-ct-text">Glas</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {GLAS_OPTS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGlasType(g)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        glasType === g
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-sm text-ct-text">
                <input
                  type="checkbox"
                  checked={multiRoom}
                  onChange={(e) => setMultiRoom(e.target.checked)}
                  className="h-4 w-4 rounded border-ct-border accent-ct-accent"
                />
                Meerdere ruimtes (≥100 m²): woonkamer / slaap / overig
              </label>

              <div>
                <p className="text-sm font-semibold text-ct-text">Constructie-proxy (tabelmodus)</p>
                <div className="mt-2 flex flex-col gap-2">
                  {CONSTRUCTIE_OPTS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setConstructie(o.id)}
                      className={`rounded-xl border px-4 py-2 text-left text-xs transition ${
                        constructie === o.id
                          ? 'border-ct-accent bg-ct-accent/10 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/40 text-ct-muted hover:border-ct-accent/25'
                      }`}
                    >
                      <span className="font-bold">{o.label}</span>
                      <span className="block text-[11px] opacity-80">{o.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-ct-text">Luchtdichtheid</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {LUCHT_OPTS.map((x) => (
                    <button
                      key={x}
                      type="button"
                      onClick={() => setLucht(x)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        lucht === x
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {x}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="demo-temp" className="flex justify-between text-sm font-semibold text-ct-text">
                  <span className="inline-flex items-center gap-1">
                    <Thermometer className="h-4 w-4 text-ct-accent" aria-hidden />
                    Binnentemperatuur
                  </span>
                  <span className="tabular-nums text-ct-accent">{binnenTemp} °C</span>
                </label>
                <input
                  id="demo-temp"
                  type="range"
                  min={19}
                  max={23}
                  step={1}
                  value={binnenTemp}
                  onChange={(e) => setBinnenTemp(Number(e.target.value))}
                  className="mt-3 h-2 w-full cursor-pointer accent-ct-accent"
                />
              </div>

              <div>
                <label htmlFor="demo-wb" className="flex justify-between text-sm font-semibold text-ct-text">
                  <span>Warmtebrug-toeslag (opaak)</span>
                  <span className="tabular-nums text-ct-accent">{warmtebrugPct}%</span>
                </label>
                <input
                  id="demo-wb"
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={warmtebrugPct}
                  onChange={(e) => setWarmtebrugPct(Number(e.target.value))}
                  className="mt-3 h-2 w-full cursor-pointer accent-ct-accent"
                />
              </div>

              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-ct-text">
                  <MapPin className="h-4 w-4 text-ct-accent" aria-hidden />
                  Postcode (klimaat)
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {POSTCODE_OPTS.map((o) => (
                    <button
                      key={o.pc}
                      type="button"
                      onClick={() => setPostcode(o.pc)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        postcode === o.pc
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {o.pc} · {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-ct-text">Prijzen (zelfde logica als site)</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(
                    [
                      { id: 'particulier' as const, label: 'Particulier · incl. btw' },
                      { id: 'installateur' as const, label: 'Zakelijk · ex. btw' },
                    ] as const
                  ).map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setSegment(o.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                        segment === o.id
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(
                    [
                      { id: 'basis' as const, label: 'Comfort Basis' },
                      { id: 'plus' as const, label: 'Comfort Plus' },
                    ] as const
                  ).map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setTier(o.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                        tier === o.id
                          ? 'border-ct-accent bg-ct-accent/12 text-ct-text'
                          : 'border-ct-border bg-ct-elevated/50 text-ct-muted hover:border-ct-accent/30'
                      }`}
                    >
                      {o.id === 'plus' ? (
                        <span className="inline-flex items-center gap-1">
                          <Sparkles className="h-3.5 w-3.5" aria-hidden />
                          {o.label}
                        </span>
                      ) : (
                        o.label
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-2xl border border-ct-border bg-ct-elevated/40 p-5">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ct-muted">
                  <Zap className="h-4 w-4 text-ct-accent" aria-hidden />
                  Uitkomst motor
                </h3>
                {displayResult ? (
                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-ct-muted">Warmtelast</dt>
                      <dd className="font-bold tabular-nums text-ct-text">{displayResult.warmtelastKW.toFixed(2)} kW</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-ct-muted">Koellast</dt>
                      <dd className="font-bold tabular-nums text-ct-text">{displayResult.koellastKW.toFixed(2)} kW</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-ct-muted">Piek</dt>
                      <dd className="font-bold tabular-nums text-ct-text">{displayResult.pieklastKWDisplay.toFixed(2)} kW</dd>
                    </div>
                    <div className="flex justify-between gap-4 border-t border-ct-border/60 pt-2">
                      <dt className="text-ct-muted">Jaar warmte (thermisch)</dt>
                      <dd className="font-semibold tabular-nums text-ct-text">
                        {Math.round(displayResult.jaarverbruikWarmteKWh)} kWh
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-ct-muted">Jaar koel</dt>
                      <dd className="font-semibold tabular-nums text-ct-text">
                        {Math.round(displayResult.jaarverbruikKoelKWh)} kWh
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4 text-xs">
                      <dt className="text-ct-muted">W/m² (gebouw)</dt>
                      <dd className="tabular-nums text-ct-text">
                        {displayResult.gebouwIntensiteit.warmte_W_m2.toFixed(1)} W/m²
                      </dd>
                    </div>
                    {displayResult.climateContext ? (
                      <div className="rounded-lg border border-ct-border/60 bg-ct-surface/60 px-3 py-2 text-xs text-ct-muted">
                        <CloudSun className="mb-1 inline h-3.5 w-3.5 text-ct-accent" aria-hidden />{' '}
                        {displayResult.climateContext.locationLabel} · HDD {Math.round(displayResult.climateContext.hdd18)} ·
                        CDD {Math.round(displayResult.climateContext.cdd24)}
                      </div>
                    ) : null}
                  </dl>
                ) : (
                  <p className="mt-4 text-sm text-ct-muted">Nog geen geldige uitkomst.</p>
                )}
              </div>

              <div className="rounded-2xl border border-ct-border bg-ct-elevated/40 p-5">
                <h3 className="text-sm font-bold uppercase tracking-wide text-ct-muted">Webprijs (indicatief)</h3>
                {snapshot.requiresQuote ? (
                  <p className="mt-3 text-sm text-ct-text">Offerte — oppervlakte boven band XL.</p>
                ) : (
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-ct-muted">Basis</dt>
                      <dd className="font-semibold tabular-nums">
                        {priceBasis != null
                          ? segment === 'particulier'
                            ? formatPriceEur(priceBasis)
                            : `${formatPriceEur(priceBasis, 0)} ex btw`
                          : '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-ct-muted">Plus</dt>
                      <dd className="font-semibold tabular-nums text-ct-accent">
                        {pricePlus != null
                          ? segment === 'particulier'
                            ? formatPriceEur(pricePlus)
                            : `${formatPriceEur(pricePlus, 0)} ex btw`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>
          </div>
        </section>

        {displayResult && ruimteRows.length > 0 ? (
          <section className="demo-reveal mt-16">
            <div className="flex items-center gap-2">
              <Table2 className="h-5 w-5 text-ct-accent" aria-hidden />
              <h2 className="text-xl font-bold text-ct-text">Per ruimte (engine-output)</h2>
            </div>
            <p className="mt-2 text-sm text-ct-muted">
              Zelfde kolommen als in het resultaat na de scan: vermogen en jaarbijdrage per zone.
            </p>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-ct-border">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-ct-border bg-ct-elevated/50 text-xs uppercase tracking-wide text-ct-muted">
                    <th className="px-4 py-3 font-bold">Ruimte</th>
                    <th className="px-4 py-3 font-bold">m²</th>
                    <th className="px-4 py-3 font-bold">Warmte kW</th>
                    <th className="px-4 py-3 font-bold">Koel kW</th>
                    <th className="px-4 py-3 font-bold">Jaar warmte kWh</th>
                  </tr>
                </thead>
                <tbody>
                  {ruimteRows.map((r) => (
                    <tr key={r.id} className="border-b border-ct-border/70 last:border-0 hover:bg-ct-elevated/20">
                      <td className="px-4 py-3 font-medium text-ct-text">{r.naam}</td>
                      <td className="px-4 py-3 tabular-nums text-ct-muted">{r.vloeroppervlakte_m2}</td>
                      <td className="px-4 py-3 tabular-nums">{(r.warmte_W / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 tabular-nums">{(r.koel_W / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 tabular-nums">{Math.round(r.jaar_warmte_kWh)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {displayResult.jipEnJannekeSamenvatting ? (
              <p className="mt-4 rounded-xl border border-ct-border/60 bg-ct-elevated/30 px-4 py-3 text-sm leading-relaxed text-ct-muted">
                {displayResult.jipEnJannekeSamenvatting}
              </p>
            ) : null}
          </section>
        ) : null}

        {displayResult && displayResult.scenarios?.length ? (
          <section className="demo-reveal mt-16 rounded-3xl border border-ct-border bg-ct-surface/80 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-ct-text">Scenario-bandbreedte</h2>
            <p className="mt-2 text-sm text-ct-muted">
              De scan toont pessimistisch / basis / optimistisch — zelfde ontwerpcondities, andere aannames in de proxy.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {displayResult.scenarios.map((s) => (
                <div key={s.id} className="rounded-2xl border border-ct-border bg-ct-elevated/40 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-ct-accent">{s.label}</p>
                  <p className="mt-1 text-[11px] text-ct-muted">{s.subtitle}</p>
                  <p className="mt-3 text-lg font-bold tabular-nums text-ct-text">{s.warmtelastKW.toFixed(2)} kW</p>
                  <p className="text-xs text-ct-muted">warmte · piek {s.pieklastKW.toFixed(2)} kW</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="demo-reveal mt-16">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-ct-accent" aria-hidden />
            <h2 className="text-xl font-bold text-ct-text">Wat de volledige tool nog doet</h2>
          </div>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {SCAN_FEATURES.map((f) => (
              <li key={f.titel} className="rounded-2xl border border-ct-border bg-ct-surface/80 p-5">
                <h3 className="font-bold text-ct-text">{f.titel}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ct-muted">{f.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="demo-reveal mt-16 rounded-3xl border border-dashed border-ct-border bg-ct-elevated/30 p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-ct-accent" aria-hidden />
            <h2 className="text-xl font-bold text-ct-text">Transparantie — wat de motor wél rekent</h2>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ct-muted">
            {ENGINE_TRANSPARENCY_BULLETS.map((b, i) => (
              <li key={i} className="flex gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-ct-success" aria-hidden />
                {b}
              </li>
            ))}
          </ul>
        </section>

        <section className="demo-reveal mt-16 grid gap-6 rounded-3xl border border-ct-border bg-gradient-to-br from-ct-elevated/50 to-ct-surface/90 p-6 sm:grid-cols-2 sm:p-8">
          <div>
            <h3 className="flex items-center gap-2 font-bold text-ct-text">
              <Sparkles className="h-5 w-5 text-ct-accent" aria-hidden />
              Comfort Basis
            </h3>
            <ul className="mt-4 space-y-2 text-sm text-ct-muted">
              {['Vast opgebouwd rapport', 'Indicatieve kW en jaarperspectief', 'Print / CSV'].map((t) => (
                <li key={t} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-ct-success" aria-hidden />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-ct-accent/30 bg-ct-accent/[0.06] p-5">
            <h3 className="flex items-center gap-2 font-bold text-ct-text">
              <Sparkles className="h-5 w-5 text-ct-accent" aria-hidden />
              Comfort Plus
            </h3>
            <ul className="mt-4 space-y-2 text-sm text-ct-muted">
              {['Alles van Basis', 'Uitgebreid leesrapport (AI op jouw data)', 'Delen met opdrachtgever/installateur'].map(
                (t) => (
                  <li key={t} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ct-accent" aria-hidden />
                    {t}
                  </li>
                )
              )}
            </ul>
          </div>
        </section>

        <div className="demo-reveal mt-16 rounded-3xl border border-ct-accent/30 bg-ct-accent/10 px-6 py-10 text-center">
          <p className="text-sm font-semibold text-ct-text">Klaar voor je echte invoer?</p>
          <p className="mx-auto mt-2 max-w-lg text-sm text-ct-muted">
            In de wizard stel je elke ruimte, ventilatie en wensen fijnmazig in — en kun je direct door naar rapport en aankoop.
          </p>
          <Link
            to="/scan"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-ct-accent px-8 py-3.5 text-sm font-bold text-white transition hover:bg-blue-500"
          >
            Start de scan
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </>
  );
}
